import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import {
  acceptInviteRequest,
  AuthErrorCode,
  type InviteInfoResponse,
  type Role,
} from '@forbiz/contracts'
import { api, ApiError, useAuth } from '@forbiz/web-kit'

import styles from './InviteAcceptPage.module.css'

/** 초대에 담긴 역할을 사용자에게 보여줄 한국어 라벨로. */
const roleLabel: Record<Role, string> = {
  COMPANY_ADMIN: '기업 관리자',
  COMPANY_MEMBER: '기업 담당자',
  OPERATOR: '포비즈 운영자',
  VENDOR_MEMBER: '화환 업체 담당자',
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; info: InviteInfoResponse }
  | { status: 'invalid'; message: string }

/**
 * 초대 수락 화면(공개 경로 /invite).
 *
 * 초대 링크(?token=)를 연 사람이 자기 정보를 확인하고 비밀번호를 설정한다.
 * 이 시점에 계정이 활성화되고 자동 로그인된다(설계 명세서 1.2).
 *
 * 자동 로그인 상태 반영: useAuth.acceptInvite가 accept 응답의 user로 컨텍스트를
 * 채운다. accept가 세션 쿠키를 심고 user도 돌려주므로, 별도 login 재호출 없이
 * 한 번의 요청으로 끝난다. 그래야 RequireAuth가 인증 상태를 인식한다.
 */
export function InviteAcceptPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const navigate = useNavigate()
  const { acceptInvite } = useAuth()

  const [load, setLoad] = useState<LoadState>({ status: 'loading' })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // 마운트 시 초대 정보를 조회한다. 토큰이 없거나 무효/만료면 에러 화면으로 간다.
  useEffect(() => {
    if (!token) {
      setLoad({ status: 'invalid', message: '초대 링크에 토큰이 없습니다.' })
      return
    }
    let alive = true
    api
      .get<InviteInfoResponse>(`/api/auth/invite?token=${encodeURIComponent(token)}`)
      .then((info) => {
        if (alive) setLoad({ status: 'ready', info })
      })
      .catch(() => {
        // 404(무효/만료)든 그 밖의 오류든, 사용자에게는 동일하게 "쓸 수 없는 초대"로 안내한다.
        if (alive)
          setLoad({ status: 'invalid', message: '만료되었거나 유효하지 않은 초대입니다.' })
      })
    return () => {
      alive = false
    }
  }, [token])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return // 이중 제출 재진입 가드(버튼 disabled에만 의존하지 않는다)
    setFieldErrors({})
    setFormError('')

    // 제출 전 클라 검증. 비밀번호 정책은 계약(acceptInviteRequest)이 강제한다.
    const parsed = acceptInviteRequest.safeParse({ token, password })
    const errs: Record<string, string> = {}
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0])
        if (key && !errs[key]) errs[key] = issue.message
      }
    }
    // 비밀번호 확인 불일치는 클라에서만 막는다(서버로 보내지 않는다).
    if (password !== confirm) {
      errs['confirm'] = '비밀번호가 일치하지 않습니다'
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }

    setSubmitting(true)
    try {
      // accept가 세션 쿠키를 심고 응답의 user로 컨텍스트를 채운다(요청 1회).
      await acceptInvite(token, password)
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === AuthErrorCode.VALIDATION_FAILED && err.fields?.['password']) {
          setFieldErrors({ password: err.fields['password'] })
        } else {
          setFormError(err.message)
        }
        // 만료·무효면 폼을 더 이상 쓸 수 없다. 에러 화면으로 전환한다.
        if (err.code === AuthErrorCode.INVITE_EXPIRED || err.code === AuthErrorCode.INVITE_INVALID) {
          setLoad({ status: 'invalid', message: err.message })
        }
      } else {
        setFormError('일시적인 오류가 발생했습니다. 잠시 후 다시 시도하세요')
      }
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.brand}>포비즈</h1>
        <p className={styles.subtitle}>초대 수락</p>

        {load.status === 'loading' && (
          <p className={styles.status} role="status">
            초대 정보를 확인하는 중…
          </p>
        )}

        {load.status === 'invalid' && (
          <div role="alert">
            <p className={styles.errorTitle}>초대를 사용할 수 없습니다</p>
            <p className={styles.errorBody}>{load.message}</p>
            <div className={styles.footer}>
              <Link to="/login">로그인 화면으로</Link>
            </div>
          </div>
        )}

        {load.status === 'ready' && (
          <form onSubmit={onSubmit} noValidate>
            <div className={styles.invite}>
              <p className={styles.inviteLead}>
                <span className={styles.inviteOrg}>{load.info.organizationName}</span> 조직에{' '}
                <span className={styles.inviteRole}>{roleLabel[load.info.role]}</span>
                (으)로 초대되었습니다.
              </p>
              <p className={styles.inviteEmail}>
                {load.info.name} · {load.info.email}
              </p>
            </div>

            {formError && (
              <div className={styles.formError} role="alert">
                {formError}
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label} htmlFor="password">
                비밀번호
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={fieldErrors['password'] ? true : undefined}
                disabled={submitting}
              />
              {fieldErrors['password'] ? (
                <p className={styles.fieldError}>{fieldErrors['password']}</p>
              ) : (
                <p className={styles.hint}>10자 이상, 영문·숫자·기호 중 2종 이상</p>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="confirm">
                비밀번호 확인
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-invalid={fieldErrors['confirm'] ? true : undefined}
                disabled={submitting}
              />
              {fieldErrors['confirm'] && (
                <p className={styles.fieldError}>{fieldErrors['confirm']}</p>
              )}
            </div>

            <button
              type="submit"
              data-variant="primary"
              className={styles.submit}
              disabled={submitting}
            >
              {submitting ? '설정 중…' : '비밀번호 설정하고 시작하기'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
